<template>
  <div class="intro-screen">
    <div class="title">
      <span>BUILDING&nbsp;</span>
        <transition name="title">
          <div
            class="changing"
            :key="index"
            v-for="(state, index) in stoneStates"
            :style="{color: '#' + state.color.toString(16)}"
            v-if="index === stoneShapeIndex"
          >
            <span>HOPES</span>
          </div>
        </transition>
    </div>
    <canvas class="three-canvas" :class="{'canvas-clickable': stoneMorphComplete}" ref="threeCanvas" @click="onStoneClick"></canvas>
    <transition>
      <el-button class="start-btn" v-if="stoneMorphComplete" @click="$emit('update:mode', 'wheel')" :style="{color: '#' + stoneStates[stoneShapeIndex].color.toString(16)}">START</el-button>
      <el-button v-else class="loading-text">LOADING...</el-button>
    </transition>
    <transition>
      <div class="desktop-ctas" v-if="stoneMorphComplete">
        <p> Take this to a whole different level!<br>Download the immersive AR app</p>

        <div class="download-container" @mouseleave="onMouseLeave" @mouseenter="onMouseEnter">
          <transition mode="out-in" name="download-animation">
            <DownloadIcon
              v-if="!showDownload"
              class="download-icon"
            />
            <div class="hidden-button" v-else>
              <a href="https://play.google.com/store/apps/details?id=it.accurat.BuildingHopesM" target="_blank" class="google-play"><img src="../assets/img/google-play.png" /></a>
              <a href="https://itunes.apple.com/app/building-hopes/id1424884572?ls=1&mt=8" target="_blank" class="app-store"><img src="../assets/img/app-store.png" /></a>
            </div>
          </transition>
        </div>
      </div>
    </transition>

    <h2 class="logo-desktop"><AccuratLogo class="accurat-logo" /> <span class="sep"></span> <GoogleLogo class="google-logo" /></h2>
    <h2 class="logo-mobile"><AccuratLogoMobile class="accurat-logo" /> <span class="sep"></span> <GoogleLogoMobile class="google-logo" /></h2>
  </div>
</template>

<script>
/* global THREE */
import Platform from '@/platform'
import HopeConceptStone from '@/atoms/HopeConceptStone'
import HopeStoneTexture from '@/atoms/HopeStoneTexture'
import RAF from '@/utils/RAF'
import rand from 'random-seed'
import delay from 'lodash/delay'
import anime from 'animejs'
import ElButton from 'element-ui/packages/button'
import applyToonLighting from '@/utils/applyToonLighting'
import DownloadIcon from '@/assets/img/about_5.svg'

import AccuratLogo from '@/assets/img/desktop_logo1.svg'
import GoogleLogo from '@/assets/img/desktop_logo2.svg'

import AccuratLogoMobile from '@/assets/img/mobile_logo1.svg'
import GoogleLogoMobile from '@/assets/img/mobile_logo2.svg'

// import Viewport from '@/mixins/Viewport'

export default {
  name: 'IntroScreen',
  // mixins: [Viewport],
  components: {
    ElButton,
    DownloadIcon,
    AccuratLogo,
    GoogleLogo,
    GoogleLogoMobile,
    AccuratLogoMobile
  },
  props: {
    mode: {
      required: true,
      type: String
    }
  },
  data: function () {
    return {
      stoneShapeIndex: 0,
      stoneStates: [
        {color: 0x925DE8},
        {color: 0x2B71E2},
        {color: 0xFFBE2E},
        {color: 0xFF85CE},
        {color: 0xFF556A},
        {color: 0x48C4EF},
        {color: 0xFF8E55},
        {color: 0x64E2BB}
      ],
      showDownload: false,
      width: 200,
      height: 200,
      stoneChangeDelay: 1000,
      stoneChangeSpeed: 500,
      // stoneChangeDelay: 5000,
      // stoneChangeSpeed: 700,
      stoneColorAlpha: 0,
      stoneMorphComplete: false
    }
  },

  mounted: function () {
    if (!Platform.prerenderer) {
      this._renderer = new THREE.WebGLRenderer({
        antialias: true,
        canvas: this.$refs.threeCanvas,
        alpha: true
      })
      this._clock = new THREE.Clock()
      this._renderer.toneMapping = THREE.LinearToneMapping
      this._renderer.setClearColor(0xffffff, 0)
      this._renderer.setPixelRatio(window.devicePixelRatio)
      this._renderer.setSize(this.width, this.height)

      this._scene = new THREE.Scene()

      this._camera = new THREE.PerspectiveCamera(50, this.width / this.height, 0.001, 10)
      this._camera.position.set(0, 0, 1)

      this._directionalLight = new THREE.DirectionalLight()
      this._directionalLight.position.set(0, 5, 5)
      this._scene.add(this._directionalLight)

      let concept = {
        activity: 0.5,
        category: 0,
        id: 0,
        importance: 0.5,
        label: 'intro',
        trend: -1
      }

      this._texture = new HopeStoneTexture(this.stoneStates[this.stoneShapeIndex].color, 1)

      this._conceptStone = new HopeConceptStone(concept, new THREE.MeshToonMaterial({
        specular: new THREE.Color(0x000000),
        shininess: 0,
        map: this._texture,
        transparent: false,
        morphTargets: true
      }), false, false)

      this._conceptStone.material.onBeforeCompile = applyToonLighting

      let randomGenerator = rand.create()
      this._conceptStone.geometry = this.generateGeometry(randomGenerator)

      for (let i = 0; i < this.stoneStates.length; i++) {
        let newGeo = this.generateGeometry(randomGenerator)
        this._conceptStone.geometry.morphTargets.push({ name: 'target' + i, vertices: newGeo.vertices, influence: i === 0 ? 1 : 0 })
      }

      this._conceptStone.updateMorphTargets()
      this._conceptStone.setVisible(true)
      this._scene.add(this._conceptStone)

      delay(this.changeStone, this.stoneChangeDelay)

      RAF.add(this.render)
    }
  },

  beforeDestroy: function () {
    delay(() => {
      this._scene.traverse(this.destroySceneChildren)
      this._renderer.dispose()
      this._renderer.forceContextLoss()
      RAF.remove(this.render)
      this._destroyed = true
    }, 2000)
  },

  computed: {

  },

  methods: {
    onStoneClick () {
      if (this.stoneMorphComplete) {
        this.$emit('update:mode', 'wheel')
      }
    },

    onMouseEnter () {
      this.showDownload = true
    },

    onMouseLeave () {
      this.showDownload = false
    },

    generateGeometry: function (randomGenerator) {
      let vTotal = new THREE.Vector3()
      let vNumber = 0
      let geometry = new THREE.IcosahedronGeometry(1, 0)

      geometry.rotateX(randomGenerator.floatBetween(-Math.PI / 4, Math.PI / 4))
      geometry.rotateZ(randomGenerator.floatBetween(-Math.PI / 4, Math.PI / 4))
      // geometry.rotateY(randomGenerator.floatBetween(0, Math.PI))
      // geometry.rotateZ(randomGenerator.floatBetween(0, Math.PI))

      geometry.vertices.forEach((vertex) => {
        vertex.multiplyScalar(randomGenerator.floatBetween(0.3, 1))
        vTotal.add(vertex)
        vNumber++
      })

      vTotal.divideScalar(vNumber)
      geometry.translate(-vTotal.x, -vTotal.y, -vTotal.z)

      let sub = new THREE.SubdivisionModifier(2)
      sub.modify(geometry)

      return geometry
    },

    changeStone: function () {
      if (this._destroyed) return
      this.stoneShapeIndex++
      if (this.stoneShapeIndex < this.stoneStates.length) {
        let prevIndex = this.stoneShapeIndex === 0 ? this._conceptStone.geometry.morphTargets.length - 1 : this.stoneShapeIndex - 1
        anime({
          targets: this._conceptStone.geometry.morphTargets[prevIndex],
          influence: 0,
          duration: this.stoneChangeSpeed,
          easing: 'easeOutCubic'
        })
        anime({
          targets: this._conceptStone.geometry.morphTargets[this.stoneShapeIndex],
          influence: 1,
          duration: this.stoneChangeSpeed,
          easing: 'easeOutCubic'
        })
        // let state = this.stoneStates[this.stoneShapeIndex]
        // this._texture.draw(state.color, 1)

        this.stoneColorAlpha = 0
        anime({
          targets: this,
          stoneColorAlpha: 1,
          duration: this.stoneChangeSpeed,
          easing: 'easeOutCubic',
          update: (anim) => {
            // if (!this.stoneStates[prevIndex] || this.stoneStates[prevIndex]) {
            //   return
            // }

            let prevColor = new THREE.Color(this.stoneStates[prevIndex].color)
            let nowColor = new THREE.Color(this.stoneStates[this.stoneShapeIndex].color)
            prevColor.lerp(nowColor, anim.animations[0].currentValue)
            this._texture.draw(prevColor.getHex(), 1)
          }
        })

        delay(this.changeStone, this.stoneChangeDelay)
      } else {
        if (Platform.mobile) {
          this.$emit('update:mode', 'wheel')
        } else {
          this.stoneMorphComplete = true
          this.stoneShapeIndex = -1
          this.changeStone()
          // delay(this.changeStone, this.stoneChangeDelay)
        }
      }
    },
    render: function () {
      // if (!this.active) return

      this._conceptStone.geometry.morphTargets.forEach((target, i) => {
        this._conceptStone.morphTargetInfluences[i] = target.influence
      })

      let delta = this._clock.getDelta()
      if (this._conceptStone) {
        this._conceptStone.update(null, delta, true)
      }
      this._renderer.render(this._scene, this._camera, null, true)
    },
    onResize: function () {

      // if (this._renderer) {
      //   this._renderer.setPixelRatio(window.devicePixelRatio)
      //   this._renderer.setSize(this.viewPort.width, this.viewPort.height)
      // }

      // if (this._camera) {
      //   this._camera.aspect = this.viewPort.width / this.viewPort.height
      //   this._camera.updateProjectionMatrix()
      // }
    },
    destroySceneChildren: function (value) {
      if (typeof value.destroy === 'function') {
        // console.log('destroy')
        value.destroy()
      } else {
        if (value.geometry) {
          value.geometry.dispose()
        }
        if (value.material) {
          if (value.material.map) {
            value.material.map.dispose()
          }
          value.material.dispose()
        }
      }
    }
  },

  watch: {
    stoneMorphComplete: function () {
      if (Platform.mobile) {
        this.$emit('update:mode', 'wheel')
      }
    },
    viewPort: function (value) {
      this.onResize()
    }
  }
}
</script>

<style scoped lang="scss">
@import 'variables';
@import 'mixins';

.intro-screen {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
}

.accurat-logo,
.google-logo {
  fill: #565656;
  stroke: transparent;

  display: block;
  margin: 8px auto;

  @include breakpoint(#{$bp-sm}px) {
    display: inline-block;
    margin: auto;
    margin-left: 10px;
  }
}

.logo-desktop .accurat-logo {
  height: 17px;
}

.logo-desktop .google-logo {
  height: 20px;
}

.logo-mobile .accurat-logo {
  height: 30px;
}

.logo-mobile .google-logo {
  height: 36px;
}

span.plus {
  font-size: 13px;
  display: inline-block;
  transform: translateY(-2px);
  margin-left: 5px;
  margin-right: 5px;
}

.sep {
  display: block;
  height: 10px;

  @include breakpoint(#{$bp-sm}px) {
    display: inline-block;
    width: 40px;
    height: auto;
  }
}

.logo-desktop {
  display: none;

  @include breakpoint(#{$bp-sm}px) {
    display: block;
  }
}

.logo-mobile {
  display: block;

  @include breakpoint(#{$bp-sm}px) {
    display: none;
  }
}

.loading-text {
  position: absolute;
  left: 50%;
  top: 55%;
  transform: translate(-50%, -50%);

  box-shadow: none;
  background: none;
  letter-spacing: 3px;

  user-select: none;
  pointer-events: none;
  cursor: none;
}

.three-canvas {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -80%);

  user-select: none;
  pointer-events: none;
  cursor: none;

  &.canvas-clickable {
    cursor: pointer;
    pointer-events: all;
  }
}

.title {
  position: absolute;
  text-align: center;
  top: 15%;
  left: 50%;
  font-size: 24px;
  font-weight: 700;
  letter-spacing: 2px;
  color: $c-black;
  transform: translate(-80%, -50%);
  user-select: none;
}

.changing {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translate(100%, -50%) rotate(-45deg);
  overflow: hidden;
  width: 100px;
  height: 100px;

  span {
    position: absolute;
    transform: translate(-50%, -50%) rotate(45deg);
    top: 50%;
    left: 50%;
  }
}

h2 {
  position: absolute;
  width: 100%;
  text-align: center;
  bottom: 20px;
  font-size: 16px;
  font-weight: 600;
  color: #848484;
}

.desktop-ctas {
  position: absolute;
  width: 100%;
  text-align: center;
  top: 50%;
  left: 50%;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 1px;
  color: $c-gray;
  transform: translate(-50%, 0);
  margin-top: 150px;

  display: none;

  a {
    position: relative;
    width: 200px;
    display: inline-block;
    img {
      width: 100%;
      height: auto;
    }
  }

  @include breakpoint(#{$bp-sm}px) {
    display: block;
  }
}

.download-icon {
  width: 80px;
}

.start-btn {
  position: absolute;
  left: 50%;
  top: 55%;
  transform: translate(-50%, -50%);

  box-shadow: none;
  background: none;
  letter-spacing: 3px;
}

.download-container {
  cursor: pointer;
  pointer-events: all;

  margin: 30px auto 0px;
  display: table;
}

.title-enter-active {
  transition: height 0.5s;
}
.title-leave-active {
  transition: opacity 0.5s;
}

.title-enter {
  height: 0;
}
.title-leave-to {
  opacity: 1;
}

.download-animation-enter-active,
.download-animation-leave-active {
  transition: opacity 0.23s $ease-out-quart, transform 0.23s $ease-out-quart;
  
  .google-play,
  .download-icon,
  .app-store {
    transition: opacity 0.2s $ease-out-quart, transform 0.2s $ease-out-quart;
  }

  .app-store {
    transition-delay: 0.03s;
  }
}

.download-animation-enter,
.download-animation-leave-to {
  opacity: 0;
  transform: translateY(10px);

  .google-play,
  .download-icon,
  .app-store {
    opacity: 0;
    transform: translateY(10px);
  }
}

</style>



// WEBPACK FOOTER //
// src/organisms/IntroScreen.vue